import React from "react";
import dayjs from "dayjs";
import { serverNow } from "./serverClock.js";
import duration from "dayjs/plugin/duration";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(duration);
dayjs.extend(relativeTime);

// Number of decimal digits in k*2^n - 1. The -1 only ever changes the count
// for degenerate powers of ten, which never occur for the k we search, so the
// standard logarithmic count is exact here and stays fast for any n.
export function digitCount(k, n) {
  return Math.floor(n * Math.log10(2) + Math.log10(k)) + 1;
}

export function digits(k, n) {
  return formatNumber(digitCount(k, n));
}

// The candidate as plain text: no separators, ASCII * and ^. This is the one form that can be
// pasted somewhere and run — it is the literal argument the client hands PRST
// (progressui/manager.go) — so everything that copies a candidate copies this, and the prettier
// form with · and a superscript stays for display only.
export function candidateExpression(k, n) {
  return `${k}*2^${n}-1`;
}

export function formatProgress(percent) {
  if (percent !== null) {
    return Number(percent / 100).toLocaleString(undefined, {
      style: "percent",
      minimumFractionDigits: 1,
    });
  } else {
    return percent;
  }
}

export const memory = (bytes) => {
  if (!bytes) return "";
  const gb = bytes / 1024 ** 3;
  return gb >= 1
    ? `${Math.round(gb)} GB`
    : `${Math.round(bytes / 1024 ** 2)} MB`;
};

export function ago(date) {
  var ms = dayjs(date).diff(serverNow());
  if (ms < 0) {
    return dayjs.duration(ms).humanize(true);
  }
  return "-";
}

/** The mirror of ago() for a date ahead of us: "in 4 days". "-" once it has passed. */
export function until(date) {
  var ms = dayjs(date).diff(serverNow());
  if (ms > 0) {
    return dayjs.duration(ms).humanize(true);
  }
  return "-";
}

export function interval(start, end) {
  if (!start) return "";

  var s = dayjs(start);
  var e = end ? dayjs(end) : dayjs();
  var ms = e.diff(s);

  if (ms < 0) return "-";

  var dur = dayjs.duration(ms);
  return formatDuration(dur);
}

function formatDuration(d) {
  const parts = [
    [Math.floor(d.asDays()), "d"],
    [d.hours(), "h"],
    [d.minutes(), "m"],
    [d.seconds(), "s"],
  ];

  const firstNonZero = parts.findIndex(([v]) => v !== 0);
  if (firstNonZero === -1) return "0s";

  return parts
    .slice(firstNonZero)
    .map(([v, u], idx) => {
      const value = idx === 0 ? String(v) : String(v).padStart(2, "0");
      return `${value}${u}`;
    })
    .join(" ");
}

export function formatSeconds(seconds) {
  if (seconds == null || seconds === 0) return null;
  return formatDuration(dayjs.duration(seconds * 1000));
}

// Two units at most, for prose rather than for a column: "1h 37m", "13s". A test that ran for an
// hour and a half is not described any better by its seconds, and the extra digits read as
// precision about something nobody measured that finely.
export function formatCompactDuration(seconds) {
  if (seconds == null || seconds <= 0) return null;
  if (seconds >= 3600) {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.round((seconds % 3600) / 60);
    return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  }
  if (seconds >= 60) {
    const minutes = Math.floor(seconds / 60);
    const rest = Math.round(seconds % 60);
    return rest > 0 ? `${minutes}m ${rest}s` : `${minutes}m`;
  }
  return `${Math.round(seconds)}s`;
}

export function liveComputeSeconds(candidate) {
  if (candidate.compute_seconds == null) return null;
  // compute_seconds is as-of the reading's measurement instant, so grow it from
  // progress_date (the same instant) when we have it, falling back to
  // modified_date for older clients that don't report a measurement time.
  const anchor = candidate.progress_date ?? candidate.modified_date;
  if (candidate.completed_date || !anchor) return candidate.compute_seconds;
  const elapsed = serverNow().diff(dayjs(anchor), "second");
  return candidate.compute_seconds + Math.max(0, elapsed);
}

// Estimated seconds remaining, counting down to a fixed completion instant that
// is re-projected on each progress update. At an update we project the total
// compute time linearly from progress (each fermat/LLR iteration takes roughly
// constant time, so progress advances linearly with compute time), giving a
// completion instant of progress_date + compute_seconds·(100−p)/p; between
// updates we subtract the time elapsed since that instant, so the value ticks
// down rather than climbing.
//
// The anchor is progress_date (when the reading was actually true), NOT
// modified_date (when the batched report arrived, up to a report-interval later):
// on a short job that lag dominates. Elapsed is measured with serverNow() so it
// stays on the server clock rather than the browser's. Any progress above zero
// gives an estimate — the first reading is enough, and it sharpens as later
// readings arrive. Null at zero progress (nothing to project from), once
// complete, and when progress_date is absent (older client, or no reading yet)
// — we hide the ETA rather than anchor it on receipt time.
export function etaSeconds(candidate) {
  const { compute_seconds: computeSeconds, progress, progress_date } = candidate;
  if (computeSeconds == null || progress == null || !progress_date || progress <= 0 || progress >= 100) {
    return null;
  }
  const projectedRemaining = (computeSeconds * (100 - progress)) / progress;
  const sinceUpdate = Math.max(0, serverNow().diff(dayjs(progress_date), "second"));
  return Math.max(0, projectedRemaining - sinceUpdate);
}

// A client reports progress every few minutes (5 by default, -r on the client),
// so a bar driven only by reported readings sits still and then jumps. Between
// readings we walk it along the same linear projection the ETA counts down on:
// progress advances at progress/compute_seconds per second, so `elapsed` seconds
// past a reading of p the bar is at p·(1 + elapsed/compute_seconds).
//
// It is a guess, so it is bounded three ways. It stops once the reading is
// STALE_PROGRESS_SECONDS old: a client that has gone quiet should show what we
// actually know rather than a bar that creeps on forever, and the display drops
// back to the last reported reading (which may step the bar backwards — that
// step is the point, it says the extrapolation was never confirmed). It never
// reaches 100, because progress of 100 with no completed_date is what marks a
// job stuck. And it only ever moves the bar forwards, never behind the reading.
const STALE_PROGRESS_SECONDS = 15 * 60; // 3x the client's default report interval
const EXTRAPOLATED_PROGRESS_CEILING = 99.9;

export function liveProgress(candidate) {
  const { compute_seconds: computeSeconds, progress, progress_date, completed_date } = candidate;
  if (
    progress == null ||
    completed_date ||
    !progress_date ||
    !computeSeconds ||
    progress <= 0 ||
    progress >= 100
  ) {
    return progress;
  }
  const elapsed = serverNow().diff(dayjs(progress_date), "second");
  if (elapsed <= 0 || elapsed > STALE_PROGRESS_SECONDS) return progress;
  return Math.min(progress * (1 + elapsed / computeSeconds), EXTRAPOLATED_PROGRESS_CEILING);
}

export function formatNumber(n, zeroValue) {
  if (n == 0 && zeroValue) {
    return "-";
  } else if (Number.isInteger(n)) {
    return n.toLocaleString(undefined);
  } else {
    return n;
  }
}

/** The letters after a number to make it ordinal, "st" for 1 or 21, "th" for 11, as plain text. */
export function ordinalSuffix(n) {
  const abs = Math.abs(Math.trunc(n));
  const mod100 = abs % 100;
  const mod10 = abs % 10;
  if (mod100 >= 11 && mod100 <= 13) return "th";
  if (mod10 === 1) return "st";
  if (mod10 === 2) return "nd";
  if (mod10 === 3) return "rd";
  return "th";
}

/**
 * @param n     the number to make ordinal
 * @param group whether to group thousands. Off for values that live in a fixed-width slot: a
 *              t5k rank stops at 5000, so "4809th" fits the same six characters as the longest
 *              prover code, while "4,809th" would not.
 */
export function ordinal(n, { group = true } = {}) {
  if (n == null || n === "") return "";

  const num = Number(n);
  if (!Number.isFinite(num)) return n;

  const suffix = ordinalSuffix(num);
  const formatted = Number.isInteger(num)
    ? (group ? num.toLocaleString(undefined) : String(num))
    : n;
  if (num === 0) return formatted;

  return React.createElement(React.Fragment, null, formatted, React.createElement("sup", null, suffix));
}

const warn = dayjs.duration(1000 * 60 * 60 * 10);
const danger = dayjs.duration(1000 * 60 * 60 * 24);

// How long ago the last reading was actually true, on the server clock. progress_date is the
// measurement instant; modified_date is when the batched report arrived, and is all an older
// client gives us. Null when the job has neither.
function readingAgeSeconds(candidate) {
  const anchor = candidate.progress_date ?? candidate.modified_date;
  if (!anchor) return null;
  return Math.max(0, serverNow().diff(dayjs(anchor), "second"));
}

export function candidateState(candidate) {
  if (candidate.prime) {
    return "prime";
  }
  if (candidate.residual) {
    return "residual";
  }
  // 100% with no result recorded is the shape of a job whose arithmetic finished and whose
  // answer never arrived — but it is also the shape of one that is about to report normally.
  // The 100 comes from a progress report and the result comes from a separate call after it,
  // so the two are briefly out of step at the end of every job that reports a full reading,
  // which on a short job is most of what the row ever shows. Waiting until the reading has
  // stood at 100 for STALE_PROGRESS_SECONDS keeps that ordinary gap out of the red, and it is
  // the same age at which the bar stops extrapolating from a reading: the stripe and the bar
  // give up on a job at the same moment rather than on two unrelated clocks.
  if (candidate.progress == 100 && candidate.completed_date == null) {
    const age = readingAgeSeconds(candidate);
    if (age == null || age >= STALE_PROGRESS_SECONDS) {
      return "stuck";
    }
  }
  if (!candidate.completed_date) {
    var s = dayjs(candidate.modified_date);
    var e = serverNow();
    var d = e.diff(s);
    var dur = dayjs.duration(d);
    if (dur > danger) {
      return "very-overdue";
    } else if (dur > warn) {
      return "overdue";
    } else {
      return "other";
    }
  } else {
    return "other";
  }
}

export const initials = (displayName) =>
  displayName
    ?.split(" ")
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2) ?? "?";

export const nameColor = (displayName) => {
  if (!displayName) return "hsl(220, 60%, 40%)";
  let hash = 0;
  for (let i = 0; i < displayName.length; i++) {
    hash = displayName.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue}, 60%, 40%)`;
};

// TeraOps is the site's unit for ops, everywhere a member reads a number: the
// leaderboards, profiles, teams, challenges and the dashboard. Rendered as a
// whole number so a column can be read down and compared against any other page.
//
// This replaced a formatter that chose MOPS/GOPS/TOPS/POPS by magnitude. That
// gave one row of a table a different unit from the row below it, so a larger
// value could print as the smaller-looking number. Callers holding raw ops
// divide by 1e12 on the way in; the aggregates the API returns are already
// TeraOps.
export function formatTeraOps(teraOps) {
  return Number(teraOps || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });
}

